/**
 * Réglages (D§3).
 *
 * Les puces FR/EN vivaient en clair dans le HUD, posées là pendant le jalon. C'était
 * un reste d'outillage : deux boutons qui prennent en permanence le coin haut-droit de
 * l'écran pour un choix qu'on fait une fois. Elles rejoignent ici le système
 * d'interface — un bouton d'icône dans la barre haute, un `Modal` de la bibliothèque.
 *
 * Le choix de langue reste accessible sans compte et sans quitter la partie : c'est un
 * réglage, pas une fonctionnalité cachée.
 */

import { getLocale, onLocaleChange, setLocale, t } from '@/i18n';
import type { Locale, TranslationKey } from '@/i18n';
import { createButton, createTabs } from './components';
import { createModal } from './components/overlays';

const LOCALES: readonly {
  readonly id: Locale;
  readonly labelKey: 'settings.language.fr' | 'settings.language.en';
}[] = [
  { id: 'fr', labelKey: 'settings.language.fr' },
  { id: 'en', labelKey: 'settings.language.en' },
];

/** Les quatre sections de la boîte, dans l'ordre où on les cherche. */
export type SettingsSection = 'language' | 'account' | 'preferences' | 'about';

export interface SettingsHandle {
  open(): void;
  /** Ajoute une ligne en pied de boîte (version, crédits…). */
  /**
   * Ajoute une ligne dans une SECTION nommée.
   *
   * Par défaut « Préférences » — la section fourre-tout de ce qui se règle une fois.
   */
  appendRow(row: HTMLElement, section?: SettingsSection): void;
  destroy(): void;
}

/**
 * Boîte de réglages, sans bouton d'ouverture.
 *
 * Le déclencheur appartient au bandeau haut (`ui/hud`), qui le compose avec la ligue et
 * les visiteurs comme le fait la maquette. Une boîte qui apporterait aussi son bouton
 * imposerait sa position dans la mise en page — exactement l'erreur que la boutique
 * faisait avec son tiroir.
 */
export function createSettings(): SettingsHandle {
  const modal = createModal({
    title: t('settings.title'),
    icon: 'gear',
  });

  /*
    Le modal doit être MONTÉ : `createModal` fabrique l'élément, il ne le pose pas.
    Sans cette ligne il n'existe nulle part dans le document, et `open()` bascule une
    classe sur un nœud que personne n'affiche — le clic semblait sans effet.

    Monté sur `body` et non dans le HUD : une boîte de dialogue doit couvrir toute la
    page, et un parent en `pointer-events: none` la rendrait inerte.
  */
  /*
    Une classe à NOUS sur la modale : les réglages sont une LISTE, pas une annonce. La
    modale générique centre son contenu — parfait pour « Nouvelle ère · +0,6 de bonus »,
    illisible pour douze lignes de réglages, qui se lisent en colonne alignée à gauche.
    Sans cette classe, il faudrait dé-centrer chaque élément un par un, et le prochain
    ajout arriverait centré.
  */
  modal.el.classList.add('settings-modal');

  document.body.append(modal.el);

  /*
    ── LA BOÎTE EST STRUCTURÉE EN SECTIONS ───────────────────────────────────

    Elle était une PILE : deux boutons de langue pleine largeur mangeant un tiers de
    l'écran, un lien légal souligné flottant au milieu, puis le compte, les préférences,
    la version et l'installation à la suite, sans respiration ni titre. Sur 390 px, il
    fallait faire défiler pour découvrir qu'il y avait autre chose — et rien ne disait
    quoi.

    Quatre sections nommées, dans l'ordre où on les cherche : ce qu'on change souvent
    (Langue), ce qui protège la partie (Compte), ce qu'on règle une fois (Préférences),
    et ce qu'on consulte (À propos). L'appelant range ses lignes par NOM de section
    plutôt que par ordre d'appel — sans quoi l'ordre du fichier `game.ts` décidait de la
    mise en page.
  */
  const sections = new Map<string, HTMLElement>();

  function section(id: string, label: string): HTMLElement {
    const existing = sections.get(id);
    if (existing !== undefined) return existing;

    const block = document.createElement('section');
    block.className = 'settings-section';
    block.dataset.section = id;

    const heading = document.createElement('h4');
    heading.className = 'settings-section__title';
    heading.textContent = label;

    const body = document.createElement('div');
    body.className = 'settings-section__body';

    block.append(heading, body);
    modal.content.append(block);
    sections.set(id, body);
    return body;
  }

  /*
    ── LA LANGUE EST UN SÉLECTEUR SEGMENTÉ ───────────────────────────────────

    Deux boutons empilés pleine largeur se lisaient comme deux actions sans rapport —
    exactement le reproche que Benoit avait déjà fait aux premiers onglets de boutique. Un
    segment dit ce qu'il est : UNE surface, découpée, dont une seule case est allumée.

    C'est `createTabs` de la bibliothèque, le même composant que les onglets de Construire,
    Ville, Progression et Boutique : le jeu n'a qu'un seul dessin pour « choisir parmi
    quelques options », et il vaut ici aussi.
  */
  /*
    ⚠️ LES SECTIONS SONT CRÉÉES DANS L'ORDRE VOULU, PAS DANS L'ORDRE D'USAGE.

    `section()` crée à la première demande : « À propos » était donc construite avant
    « Compte », simplement parce que le lien légal se pose ici et le compte plus tard, par
    `appendRow`. L'ordre à l'écran devenait celui du code — Langue, À propos, Compte,
    Préférences — c'est-à-dire personne ne l'avait choisi.

    On les déclare donc toutes, ici, dans l'ordre où on les CHERCHE : ce qu'on change
    souvent, ce qui protège la partie, ce qu'on règle une fois, ce qu'on consulte.
  */
  for (const id of ['language', 'account', 'preferences', 'about'] as const) {
    section(id, t(`settings.${id}.label` as TranslationKey));
  }

  const languageTabs = createTabs({
    tabs: LOCALES.map(({ id, labelKey }) => ({ id, label: t(labelKey) })),
    active: getLocale(),
    onChange: (id: string) => {
      setLocale(id as Locale);
    },
  });

  section('language', t('settings.language.label')).append(languageTabs.el);

  const stopListening = onLocaleChange((locale) => {
    languageTabs.setActive(locale);
  });

  /*
    ── À PROPOS : les documents légaux et l'ÉDITEUR ──────────────────────────

    Le lien légal était un texte souligné flottant entre la langue et le compte, sans
    titre au-dessus — il ne se lisait ni comme une obligation ni comme une information,
    juste comme un accident. Il descend là où on le cherche : en pied, avec la version,
    les crédits et l'éditeur.

    ⚠️ **« Jeu édité par Ludopia.fr »** (demande de Benoit, 21 août — l'extension est
    bien `.fr`, corrigée après un premier passage en `.app`). Ce n'est pas une
    décoration : nommer l'éditeur est ce qu'on attend d'un jeu qui encaisse de l'argent,
    et c'est aussi ce qui relie Villopia aux autres jeux de la maison. Le nom est un nom
    propre — il ne se traduit pas —, seule la phrase qui l'entoure passe par `t()`.
  */
  const about = section('about', t('settings.about.label'));

  const publisher = document.createElement('p');
  publisher.className = 'settings__publisher';
  publisher.append(t('settings.publisher'), ' ');

  const publisherLink = document.createElement('a');
  publisherLink.href = 'https://ludopia.fr';
  publisherLink.target = '_blank';
  publisherLink.rel = 'noopener noreferrer';
  publisherLink.textContent = 'Ludopia.fr';
  publisher.append(publisherLink);
  about.append(publisher);

  const legal = document.createElement('a');
  legal.className = 'settings__legal';
  legal.href = '/legal';
  legal.target = '_blank';
  legal.rel = 'noopener';
  legal.textContent = t('settings.legal');
  about.append(legal);

  const closeButton = createButton({
    label: t('common.close'),
    variant: 'primary',
    onClick: () => {
      modal.close();
    },
  });
  modal.actions.append(closeButton.el);

  return {
    open: () => {
      modal.open();
    },
    appendRow(row, target = 'preferences') {
      /*
        Rangé par NOM de section, pas par ordre d'appel : sans ça, l'ordre des lignes de
        `game.ts` décidait de la mise en page des réglages, et déplacer un appel de trois
        lignes suffisait à casser la structure.
      */
      section(target, t(`settings.${target}.label` as TranslationKey)).append(row);
    },
    destroy() {
      stopListening();
      modal.destroy();
    },
  };
}
